import { create } from 'zustand';

/**
 * Global Aircall Everywhere state.
 * Manages the SDK phone instance, call lifecycle, and widget visibility.
 * Lives outside React so it persists across CRM page navigations.
 */
export const useAircallEverywhereStore = create((set, get) => ({
	// SDK instance
	phoneInstance: null,
	sdkReady: false,
	isLoggedIn: false,

	// Call state
	callState: 'idle', // 'idle' | 'ringing_inbound' | 'ringing_outbound' | 'active' | 'ended'
	// activeCall.startedAt = when ringing began; activeCall.answeredAt = when the call was picked up
	// (null while still ringing). Talk duration is measured from answeredAt, never from startedAt.
	activeCall: null, // { from, to, direction, startedAt, answeredAt?, duration? }
	lastAnswerStatus: null, // 'answered' | 'disconnected' | 'refused' | null

	// Widget UI
	widgetVisible: false,
	overlayMinimized: true,

	// Setters

	setPhoneInstance: (instance) => set({ phoneInstance: instance }),
	setSdkReady: (ready) => set({ sdkReady: ready }),
	setLoggedIn: (loggedIn) => set({ isLoggedIn: loggedIn }),
	setCallState: (state) => set({ callState: state }),
	setActiveCall: (call) => set({ activeCall: call }),
	setWidgetVisible: (visible) => set({ widgetVisible: visible }),
	setOverlayMinimized: (minimized) => set({ overlayMinimized: minimized }),

	// Event handlers (called from provider)

	handleIncomingCall: (callInfos) => {
		set({
			callState: 'ringing_inbound',
			activeCall: {
				from: callInfos?.from ?? null,
				to: callInfos?.to ?? null,
				direction: 'inbound',
				startedAt: Date.now(),
				answeredAt: null,
			},
			lastAnswerStatus: null,
			overlayMinimized: false,
		});
	},

	handleOutgoingCall: (callInfos) => {
		set({
			callState: 'ringing_outbound',
			activeCall: {
				from: callInfos?.from ?? null,
				to: callInfos?.to ?? null,
				direction: 'outbound',
				startedAt: Date.now(),
				answeredAt: null,
			},
			lastAnswerStatus: null,
		});
	},

	// Fired by the SDK only for OUTBOUND calls when the remote party picks up.
	handleOutgoingAnswered: () => {
		const { activeCall } = get();
		set({
			callState: 'active',
			activeCall: activeCall ? { ...activeCall, answeredAt: Date.now() } : null,
		});
	},

	// The SDK has no `incoming_answered` event. `call_end_ringtone` is the only inbound
	// signal that tells us whether the call was picked up:
	// - 'answered'                  → transition ringing → active (stamp answeredAt for the timer)
	// - 'refused' / 'disconnected'  → the inbound call was never connected, so the SDK does NOT
	//   fire a follow-up `call_ended`.
	handleCallEndRingtone: (callInfos) => {
		const answerStatus = callInfos?.answer_status ?? null;
		const { callState, activeCall, endCall } = get();
		if (answerStatus === 'answered' && callState === 'ringing_inbound') {
			set({
				lastAnswerStatus: answerStatus,
				callState: 'active',
				activeCall: activeCall ? { ...activeCall, answeredAt: Date.now() } : null,
			});
			return;
		}
		set({ lastAnswerStatus: answerStatus });
		// Not answered → end the (never-connected) call. Duration is 0 — no talk time.
		endCall(0);
	},

	handleCallEnded: (callInfos) => {
		get().endCall(callInfos?.duration ?? null);
	},

	// Centralized call teardown — shared by `call_ended` (connected calls) and the
	// refused/disconnected branch of `call_end_ringtone` (never-connected inbound calls).
	// Idempotent: a no-op once already ended/idle, so it is safe if both events fire.
	endCall: (duration) => {
		const { callState, activeCall } = get();
		if (callState === 'ended' || callState === 'idle') return;
		// The SDK-provided duration (seconds) is authoritative talk time.
		set({
			callState: 'ended',
			activeCall: activeCall ? { ...activeCall, duration: duration ?? null } : null,
		});
		// Reset to idle after a short delay so the UI can show the "ended" state briefly.
		setTimeout(() => {
			set({ callState: 'idle', activeCall: null, lastAnswerStatus: null });
		}, 3000);
	},

	reset: () =>
		set({
			phoneInstance: null,
			sdkReady: false,
			isLoggedIn: false,
			callState: 'idle',
			activeCall: null,
			lastAnswerStatus: null,
			widgetVisible: false,
			overlayMinimized: true,
		}),
}));
